import { useEffect, useMemo, useState } from 'react';
import Reveal from '../components/Reveal';
import { useLanguage } from '../context/LanguageContext';

const imageBase = `${process.env.PUBLIC_URL}/images/media-initiative`;

/** Images marked "EN" in newPageData are English-only; "AR_EN" are shared by both versions. */
const sectionImages = {
  framework: { ar: 'introduction-ar.jpeg', en: 'introduction-en.jpeg', width: 1408, height: 768 },
  vision: { ar: 'initiative-ar.jpeg', en: 'initiative-en.jpeg', width: 1376, height: 768 },
  partnership: { ar: 'partnership-ar.jpeg', en: 'partnership-en.jpeg', width: 1376, height: 768 },
  'operational-role': {
    ar: 'operational-role.jpeg',
    en: 'operational-role.jpeg',
    width: 1376,
    height: 768,
  },
  financial: { ar: 'financial-ar.jpeg', en: 'financial-en.jpeg', width: 1408, height: 768 },
  'program-format': {
    ar: 'program-format.jpeg',
    en: 'program-format.jpeg',
    width: 1376,
    height: 768,
  },
};

const whatsappNumber = '+447832180705';
const contactEmail = 'info@hololrealestate.club';

const padStep = (n) => String(n).padStart(2, '0');
const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

/**
 * Cards sit in a two-column grid. A card that would be left alone on its row
 * (just before a wide card, or last in the list) is widened so rows never leave gaps.
 */
export function layoutBlocks(blocks) {
  const isWide = (block) => block.wide || (block.type && block.type !== 'table');
  const laidOut = blocks.map((block) => ({ ...block, span: isWide(block) }));
  let column = 0;
  laidOut.forEach((block, index) => {
    if (block.span) {
      if (column === 1) laidOut[index - 1].span = true;
      column = 0;
      return;
    }
    column = (column + 1) % 2;
  });
  if (column === 1) laidOut[laidOut.length - 1].span = true;
  return laidOut;
}

function CheckList({ items, className = '' }) {
  return (
    <ul className={`bullets mi-bullets ${className}`.trim()}>
      {items.map((item) => (
        <li key={item}>
          <span className="material-symbols-outlined check">check_circle</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Chips({ items }) {
  return (
    <ul className="mi-chips">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function Flow({ items, join, variant }) {
  const isPath = variant === 'path';
  return (
    <ol className={`mi-flow${isPath ? ' mi-flow-path' : ''}${join ? ' mi-flow-join' : ''}`}>
      {items.map((item, index) => (
        <li key={item}>
          {isPath ? <span className="mi-flow-index">{padStep(index + 1)}</span> : null}
          <span className="mi-flow-text">{item}</span>
          {index < items.length - 1 ? (
            join ? (
              <span className="mi-flow-sep" aria-hidden="true">
                {join}
              </span>
            ) : (
              <span className="material-symbols-outlined mi-flow-arrow" aria-hidden="true">
                {isPath ? 'arrow_downward' : 'arrow_forward'}
              </span>
            )
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function StatRow({ stats }) {
  return (
    <div className="mi-stat-row">
      {stats.map((stat) => (
        <div key={`${stat.value}-${stat.label}`} className="mi-stat-pill">
          <strong dir="ltr">{stat.value}</strong>
          <span>{stat.label}</span>
        </div>
      ))}
    </div>
  );
}

function Labeled({ items }) {
  return (
    <dl className="mi-labeled">
      {items.map((item) => (
        <div key={item.label} className="mi-labeled-item">
          <dt>{item.label}</dt>
          <dd>{item.text}</dd>
        </div>
      ))}
    </dl>
  );
}

function Numbered({ items }) {
  return (
    <ol className="mi-numbered">
      {items.map((item, index) => {
        const entry = typeof item === 'string' ? { text: item } : item;
        return (
          <li key={entry.label || entry.text}>
            <span className="mi-numbered-index">{index + 1}</span>
            <div>
              {entry.label ? <strong>{entry.label}</strong> : null}
              <span>{entry.text}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function CardHead({ icon, title, step }) {
  return (
    <header className="mi-card-head">
      <span className="mi-card-step">{padStep(step)}</span>
      <span className="material-symbols-outlined mi-card-icon" aria-hidden="true">
        {icon}
      </span>
      <h3>{title}</h3>
    </header>
  );
}

/**
 * Standard card. Optional fields render in this order:
 * paragraphs, stats, labeled, numbered, chips, listLabel + bullets, flowLabel + flow, closing.
 */
export function ContentCard({ block, step }) {
  const classes = ['mi-card', block.highlight ? 'mi-card-highlight' : ''].filter(Boolean).join(' ');
  return (
    <article className={classes}>
      <CardHead icon={block.icon} title={block.title} step={step} />
      {asArray(block.paragraphs).map((para) => (
        <p key={para}>{para}</p>
      ))}
      {block.stats?.length ? <StatRow stats={block.stats} /> : null}
      {block.labeled?.length ? <Labeled items={block.labeled} /> : null}
      {block.numbered?.length ? <Numbered items={block.numbered} /> : null}
      {block.chips?.length ? <Chips items={block.chips} /> : null}
      {block.listLabel ? <p className="mi-list-label">{block.listLabel}</p> : null}
      {block.bullets?.length ? <CheckList items={block.bullets} /> : null}
      {block.flowLabel ? <p className="mi-list-label">{block.flowLabel}</p> : null}
      {block.flow?.length ? (
        <Flow items={block.flow} join={block.flowJoin} variant={block.flowVariant} />
      ) : null}
      {asArray(block.closing).map((para) => (
        <p key={para} className="mi-card-closing">
          {para}
        </p>
      ))}
    </article>
  );
}

function TableCard({ block, step }) {
  return (
    <article className="mi-card">
      <CardHead icon={block.icon} title={block.title} step={step} />
      {asArray(block.paragraphs).map((para) => (
        <p key={para}>{para}</p>
      ))}
      <div className="mi-table-wrap">
        <table className={`mi-table${block.headers.length > 2 ? ' mi-table-wide' : ''}`}>
          <thead>
            <tr>
              {block.headers.map((header) => (
                <th key={header} scope="col">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => (
              <tr key={row.join('|')}>
                {row.map((cell, index) => (
                  <td key={`${row[0]}-${index}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
          {block.footer ? (
            <tfoot>
              <tr>
                <td colSpan={block.headers.length - 1}>{block.footer.label}</td>
                <td>{block.footer.value}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      {asArray(block.notes).map((note) => (
        <p key={note} className="mi-card-note">
          {note}
        </p>
      ))}
    </article>
  );
}

function PartiesCard({ block, step }) {
  return (
    <article className="mi-card">
      <CardHead icon={block.icon} title={block.title} step={step} />
      {block.intro ? <p>{block.intro}</p> : null}
      <div className="mi-parties">
        {block.items.map((party, index) => (
          <Reveal key={party.title} delay={index * 80}>
            <div className="mi-party">
              <span className="mi-party-tag">{party.tag}</span>
              <h4>{party.title}</h4>
              {party.lead ? <p className="mi-list-label">{party.lead}</p> : null}
              <Chips items={party.chips} />
              {party.note ? <p className="mi-card-closing">{party.note}</p> : null}
            </div>
          </Reveal>
        ))}
      </div>
      {asArray(block.closing).map((para) => (
        <p key={para} className="mi-card-closing">
          {para}
        </p>
      ))}
    </article>
  );
}

function PhasesCard({ block, step }) {
  return (
    <article className="mi-card">
      <CardHead icon={block.icon} title={block.title} step={step} />
      <div className="mi-phases">
        {block.items.map((phase, index) => (
          <Reveal key={phase.tag} delay={index * 90}>
            <div className="mi-phase">
              <div className="mi-phase-head">
                <span className="mi-phase-index">{index + 1}</span>
                <div>
                  <span className="mi-phase-tag">{phase.tag}</span>
                  <h4>{phase.title}</h4>
                </div>
              </div>
              <CheckList items={phase.bullets} />
              <div className="mi-phase-outcome">
                <strong>{block.outcomeLabel}</strong>
                <p>{phase.outcome}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </article>
  );
}

function SegmentsCard({ block, step }) {
  return (
    <article className="mi-card">
      <CardHead icon={block.icon} title={block.title} step={step} />
      <ol className="mi-segments">
        {block.items.map((segment, index) =>
          segment.isBreak ? (
            <li key={`break-${index}`} className="mi-segment mi-segment-break">
              <span className="material-symbols-outlined" aria-hidden="true">
                pause_circle
              </span>
              <strong>{segment.name}</strong>
              <span className="mi-segment-duration">{segment.duration}</span>
            </li>
          ) : (
            <li key={segment.tag} className="mi-segment">
              <Reveal delay={(index % 2) * 60}>
                <div className="mi-segment-card">
                  <div className="mi-segment-head">
                    <div>
                      <span className="mi-segment-tag">{segment.tag}</span>
                      <h4>{segment.name}</h4>
                    </div>
                    <span className="mi-segment-duration">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        timer
                      </span>
                      {segment.duration}
                    </span>
                  </div>
                  <ul>
                    {segment.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </li>
          )
        )}
      </ol>
      {block.signature ? (
        <div className="mi-signature">
          {block.signatureLabel ? <span>{block.signatureLabel}</span> : null}
          <blockquote>{block.signature}</blockquote>
        </div>
      ) : null}
    </article>
  );
}

const cardByType = {
  table: TableCard,
  parties: PartiesCard,
  phases: PhasesCard,
  segments: SegmentsCard,
};

function InitiativeSection({ section, index, language, stepLabel, moreLabel }) {
  // Layout is computed on the full list so cards keep their size once the section expands.
  const blocks = useMemo(() => layoutBlocks(section.blocks), [section.blocks]);
  const [expanded, setExpanded] = useState(false);
  const isCollapsible = section.previewCount > 0 && section.previewCount < blocks.length;
  const visibleBlocks = isCollapsible && !expanded ? blocks.slice(0, section.previewCount) : blocks;
  const gridId = `media-initiative-${section.id}-cards`;
  const image = sectionImages[section.id];

  return (
    <section id={`media-initiative-${section.id}`} className="section mi-section">
      <div className="container">
        <Reveal>
          <header className="mi-section-head">
            <span className="mi-section-part">
              {stepLabel} {padStep(index + 1)}
            </span>
            <span className={`eyebrow${section.eyebrowFeatured ? ' mi-eyebrow-featured' : ''}`}>
              {section.eyebrow}
            </span>
            <h2>{section.title}</h2>
            <p>{section.subtitle}</p>
          </header>
        </Reveal>

        {image ? (
          <Reveal delay={80}>
            <figure className="mi-figure">
              <img
                src={`${imageBase}/${image[language] || image.en}`}
                alt={section.title}
                width={image.width}
                height={image.height}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </figure>
          </Reveal>
        ) : null}

        <div id={gridId} className="mi-grid">
          {visibleBlocks.map((block, blockIndex) => {
            const Card = cardByType[block.type] || ContentCard;
            const card = <Card block={block} step={blockIndex + 1} />;
            // Tall composite cards reveal their inner items instead of the whole card.
            return block.type && block.type !== 'table' ? (
              <div key={block.title} className="mi-grid-item mi-grid-wide">
                {card}
              </div>
            ) : (
              <Reveal
                key={block.title}
                delay={(blockIndex % 2) * 70}
                className={`mi-grid-item${block.span ? ' mi-grid-wide' : ''}`}
              >
                {card}
              </Reveal>
            );
          })}
        </div>

        {isCollapsible && !expanded ? (
          <div className="mi-more">
            <button
              type="button"
              className="primary-btn mi-more-btn"
              aria-expanded="false"
              aria-controls={gridId}
              onClick={() => setExpanded(true)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                expand_more
              </span>
              {moreLabel}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function MediaInitiativePage() {
  const { t, language } = useLanguage();
  const page = t.mediaInitiativePage;
  const [activeSectionId, setActiveSectionId] = useState(page.sections[0]?.id || '');

  useEffect(() => {
    setActiveSectionId(page.sections[0]?.id || '');
  }, [page]);

  const scrollToSection = (id) => {
    setActiveSectionId(id);
    const node = document.getElementById(`media-initiative-${id}`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className="inner-page media-initiative-page">
      <section className="mi-hero-section">
        <div className="container">
          <Reveal>
            <div className="mi-hero">
              <div className="mi-hero-glow" aria-hidden="true" />
              <h1 className="mi-hero-title">
                {page.hero.lines.map((line, index) => (
                  <span
                    key={line.primary}
                    className={`mi-hero-line mi-hero-line-${index + 1}${
                      line.secondary ? ' mi-hero-line-bilingual' : ''
                    }`}
                  >
                    <span>{line.primary}</span>
                    {line.secondary ? (
                      <>
                        <span className="mi-hero-sep" aria-hidden="true">
                          {line.separator}
                        </span>
                        <span dir="ltr" lang="en">
                          {line.secondary}
                        </span>
                      </>
                    ) : null}
                  </span>
                ))}
              </h1>
              <p className="mi-hero-tagline">
                <span>{page.hero.tagline}</span>
                {page.hero.taglineSecondary ? (
                  <span dir="ltr" lang="en">
                    {page.hero.taglineSecondary}
                  </span>
                ) : null}
              </p>
              <div className="accent-line mi-hero-line-accent" />
              <p className="mi-hero-lead">{page.hero.lead}</p>
              <p className="mi-hero-founder">{page.hero.founder}</p>
            </div>
          </Reveal>

          <div className="mi-hero-stats">
            {page.stats.map((stat, index) => (
              <Reveal key={stat.label} delay={80 + index * 70}>
                <article className="mi-hero-stat">
                  <strong dir="ltr">{stat.value}</strong>
                  <span>{stat.label}</span>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <nav className="mi-section-nav" aria-label={page.sectionNavTitle}>
              <span className="eyebrow center">{page.sectionNavTitle}</span>
              <div className="mi-section-nav-list">
                {page.sections.map((section, index) => (
                  <button
                    key={section.id}
                    type="button"
                    className={`mi-section-nav-btn${
                      activeSectionId === section.id ? ' is-active' : ''
                    }`}
                    aria-current={activeSectionId === section.id ? 'true' : undefined}
                    onClick={() => scrollToSection(section.id)}
                  >
                    <span className="mi-section-nav-step">{padStep(index + 1)}</span>
                    <span>{section.navLabel}</span>
                  </button>
                ))}
              </div>
            </nav>
          </Reveal>
        </div>
      </section>

      {page.sections.map((section, index) => (
        <InitiativeSection
          key={section.id}
          section={section}
          index={index}
          language={language}
          stepLabel={page.stepLabel}
          moreLabel={page.moreButton}
        />
      ))}

      <section className="section mi-closing-section">
        <div className="container">
          <Reveal>
            <article className="mi-closing">
              <div className="mi-hero-glow" aria-hidden="true" />
              <span className="eyebrow">{page.closing.eyebrow}</span>
              <ul className="mi-closing-lines">
                {page.closing.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <div className="mi-closing-signature">
                <strong>{page.closing.name}</strong>
                <span>{page.closing.role}</span>
                <div className="mi-closing-links">
                  <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer">
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chat
                    </span>
                    {page.closing.whatsappLabel}: <span dir="ltr">{whatsappNumber}</span>
                  </a>
                  <a href={`mailto:${contactEmail}`}>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      mail
                    </span>
                    {page.closing.emailLabel}: <span dir="ltr">{contactEmail}</span>
                  </a>
                </div>
              </div>
            </article>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default MediaInitiativePage;
